import { Helmet } from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ComicCard from '@/components/base/ComicCard';
import HeroSection from './components/HeroSection';
import CategoryNav from './components/CategoryNav';
import FeaturedSection from './components/FeaturedSection';
import LatestSection from './components/LatestSection';
import { comics } from '@/mocks/comics';

export default function Home() {
  const hotComics = comics.filter((c) => c.isHot);

  const homeSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '韩漫之家',
        url: 'https://www.hanmanzhijia.com',
        description: '最新高清免费韩国漫画大全，热门韩漫在线阅读平台',
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '400-888-6666',
          contactType: 'customer service',
          availableLanguage: ['Chinese'],
        },
      },
      {
        '@type': 'WebSite',
        name: '韩漫之家',
        url: 'https://www.hanmanzhijia.com',
        description: '漫画免费韩漫-最新高清免费韩国漫画大全_热门韩漫在线阅读',
        potentialAction: {
          '@type': 'SearchAction',
          'target': {
            '@type': 'EntryPoint',
            urlTemplate: 'https://www.hanmanzhijia.com/search?q={search_term_string}',
          },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        name: '韩漫之家 - 首页',
        description: '最新高清免费韩国漫画大全，热门韩漫在线阅读平台',
        url: 'https://www.hanmanzhijia.com',
        isPartOf: {
          '@type': 'WebSite',
          name: '韩漫之家',
        },
      },
    ],
  };

  const remainingComics = comics.filter((c) => !c.isNew || c.isHot);

  return (
    <>
      <Helmet
        title="漫画免费韩漫-最新高清免费韩国漫画大全_热门韩漫在线阅读-韩漫之家"
        description="韩漫之家提供漫画免费韩漫服务，最新高清免费韩国漫画大全与热门作品均可在线阅读，是您的可靠选择。提供恋爱、奇幻、动作、悬疑等丰富韩漫分类，每日更新，免费畅读。"
        keywords="漫画免费韩漫,最新高清,免费韩国漫画大全,热门韩漫,在线阅读,韩国漫画,韩漫"
        canonicalUrl="https://www.hanmanzhijia.com"
        ogTitle="漫画免费韩漫-最新高清免费韩国漫画大全_热门韩漫在线阅读-韩漫之家"
        ogDescription="韩漫之家提供漫画免费韩漫服务，最新高清免费韩国漫画大全与热门作品均可在线阅读，是您的可靠选择。"
        ogImage="https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20reading%20website%20banner%20with%20artistic%20watercolor%20style%2C%20warm%20coral%20and%20cream%20tones%2C%20elegant%20composition&width=1200&height=630&seq=og-home&orientation=landscape"
        ogType="website"
        twitterCard="summary_large_image"
        schema={homeSchema}
      />
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <HeroSection />
        <div className="py-8 md:py-12 space-y-8 md:space-y-12">
          <CategoryNav />
          <FeaturedSection comics={hotComics} />
          <LatestSection comics={comics} />

          <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="bg-accent-50/50 rounded-2xl p-8 md:p-12">
              <div className="text-center mb-8">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-3">
                  为什么选择韩漫之家？
                </h2>
                <p className="text-sm text-foreground-600 max-w-2xl mx-auto">
                  我们致力于为漫画爱好者提供最优质的阅读体验
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {[
                  { icon: 'ri-shield-check-line', title: '正版授权', desc: '与多家韩国漫画平台合作，提供正版高清漫画资源' },
                  { icon: 'ri-flashlight-line', title: '极速更新', desc: '同步韩国最新连载，第一时间为您呈上最新话' },
                  { icon: 'ri-smartphone-line', title: '多端畅读', desc: '电脑、手机、平板全平台适配，随时随地畅享阅读' },
                  { icon: 'ri-heart-pulse-line', title: '完全免费', desc: '所有漫画免费阅读，无需付费，无需会员' },
                ].map((item, idx) => (
                  <div key={idx} className="text-center p-5">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-base font-heading font-bold text-foreground-950 mb-2">{item.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 rounded-full bg-secondary-500"></div>
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">
                全部漫画
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {remainingComics.slice(0, 12).map((comic) => (
                <ComicCard key={comic.id} comic={comic} variant="default" />
              ))}
            </div>
          </section>
        </div>
        <Footer />
        <ScrollToTop />
      </main>
    </>
  );
}